import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, ArrowRight, ArrowLeft, Check, Search, X } from "lucide-react";
import { toast } from "sonner";
import {
  completeMyProfile, getMyProfile, getSkillsCatalog, patchMyProfile,
} from "@/lib/afritask";

const WORK_AREA_ICONS = {
  "Content & Writing": "✍️", "Translation & Localization": "🌐",
  "Data Entry & Labeling": "📊", "Voice, Audio, Transcription": "🎙️",
  "Customer Service & Support": "🎧", "Design & Visual": "🎨",
  "Software & Tech": "💻", "Marketing & Social Media": "📢",
  "Teaching & Tutoring": "👩‍🏫", "Research & Analysis": "🧠", "Other": "✨",
};

const TASK_TYPES = [
  { code: "text_classification", label: "Text classification" },
  { code: "image_labeling", label: "Image labeling" },
  { code: "translation", label: "Translation" },
  { code: "audio_transcription", label: "Audio transcription" },
  { code: "voice_rating", label: "Voice / audio rating" },
];

export default function ProfileSetup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [catalog, setCatalog] = useState(null);
  const [profile, setProfile] = useState({});
  const [busy, setBusy] = useState(false);
  const [skillSearch, setSkillSearch] = useState("");

  useEffect(() => {
    getSkillsCatalog().then(setCatalog).catch(() => {});
    getMyProfile().then(setProfile).catch(() => {});
  }, []);

  const set = (patch) => setProfile((p) => ({ ...p, ...patch }));

  const save = async (patch) => {
    setBusy(true);
    try {
      const r = await patchMyProfile(patch);
      setProfile(r);
      return r;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const next = async (patch) => {
    if (patch) await save(patch);
    setStep((s) => s + 1);
  };

  const skip = () => setStep((s) => s + 1);

  const finish = async () => {
    setBusy(true);
    try {
      await save({});
      await completeMyProfile();
      toast.success("Profile complete!");
      navigate("/app/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Complete failed");
    } finally {
      setBusy(false);
    }
  };

  const skills = profile.skills || [];
  const filteredCatalog = useMemo(() => {
    if (!catalog) return [];
    const q = skillSearch.trim().toLowerCase();
    return catalog.groups.map((g) => ({
      ...g,
      items: q ? g.items.filter((i) => i.toLowerCase().includes(q)) : g.items,
    })).filter((g) => g.items.length > 0);
  }, [catalog, skillSearch]);

  const toggleSkill = (s) => {
    const has = skills.includes(s);
    let next;
    if (has) next = skills.filter((x) => x !== s);
    else {
      if (skills.length >= 15) { toast.error("Max 15 skills"); return; }
      next = [...skills, s];
    }
    set({ skills: next });
  };

  if (!catalog) {
    return <div className="p-8 text-center text-sm text-neutral-500">Loading…</div>;
  }

  return (
    <div className="mx-auto max-w-2xl">
      {/* Progress */}
      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between text-xs text-neutral-500">
          <span className="font-semibold uppercase tracking-widest">Step {step} of 4</span>
          <button onClick={() => navigate("/app/dashboard")} className="hover:text-neutral-800" data-testid="setup-exit">
            Skip for now
          </button>
        </div>
        <Progress value={(step / 4) * 100} className="h-1.5" />
      </div>

      {/* Step 1 — About you */}
      {step === 1 && (
        <Card data-testid="setup-step-1">
          <CardContent className="space-y-4 p-6">
            <h2 className="text-xl font-bold">About you</h2>
            <p className="text-sm text-neutral-500">Tell us a bit about yourself so tasks can match your skills better.</p>
            <div className="space-y-1.5">
              <Label>Professional summary</Label>
              <Textarea
                value={profile.summary || ""}
                onChange={(e) => set({ summary: e.target.value.slice(0, 250) })}
                placeholder="e.g. Yoruba native speaker with 4 years transcription experience for local podcasts…"
                rows={4}
                data-testid="setup-summary"
              />
              <p className="text-right text-[10px] text-neutral-400">{(profile.summary || "").length}/250</p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Years of experience</Label>
                <Select value={profile.years_experience_bucket || ""} onValueChange={(v) => set({ years_experience_bucket: v })}>
                  <SelectTrigger data-testid="setup-years"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {catalog.years_buckets.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Availability</Label>
                <Select value={profile.availability || ""} onValueChange={(v) => set({ availability: v })}>
                  <SelectTrigger data-testid="setup-availability"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {catalog.availability_options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>State</Label>
                <Select value={profile.state || ""} onValueChange={(v) => set({ state: v })}>
                  <SelectTrigger data-testid="setup-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {catalog.states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>City</Label>
                <Input value={profile.city || ""} onChange={(e) => set({ city: e.target.value })} placeholder="e.g. Ikeja" data-testid="setup-city" />
              </div>
            </div>
            <div className="flex justify-between">
              <span />
              <div className="flex gap-2">
                <Button variant="outline" onClick={skip}>Skip</Button>
                <Button onClick={() => next({ summary: profile.summary, years_experience_bucket: profile.years_experience_bucket, state: profile.state, city: profile.city, availability: profile.availability })} className="bg-amber-700 hover:bg-amber-800" disabled={busy} data-testid="setup-next-1">
                  Next <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2 — Primary work area */}
      {step === 2 && (
        <Card data-testid="setup-step-2">
          <CardContent className="space-y-4 p-6">
            <h2 className="text-xl font-bold">What&apos;s your main work area?</h2>
            <p className="text-sm text-neutral-500">Choose the one that matches you best.</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {catalog.primary_work_areas.map((w) => {
                const selected = profile.primary_work_area === w;
                return (
                  <button
                    key={w}
                    onClick={() => set({ primary_work_area: w })}
                    className={`rounded-xl border-2 p-3 text-left transition ${selected ? "border-amber-700 bg-amber-50" : "border-neutral-200 bg-white hover:border-neutral-300"}`}
                    data-testid={`work-area-${w}`}
                  >
                    <span className="text-2xl">{WORK_AREA_ICONS[w] || "✨"}</span>
                    <p className="mt-1.5 text-xs font-semibold">{w}</p>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={skip}>Skip</Button>
                <Button onClick={() => next({ primary_work_area: profile.primary_work_area })} className="bg-amber-700 hover:bg-amber-800" disabled={busy || !profile.primary_work_area} data-testid="setup-next-2">
                  Next <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3 — Skills */}
      {step === 3 && (
        <Card data-testid="setup-step-3">
          <CardContent className="space-y-4 p-6">
            <div>
              <h2 className="text-xl font-bold">Pick your skills</h2>
              <p className="text-sm text-neutral-500">Min 3, max 15. Selected: <strong>{skills.length}</strong>/15</p>
            </div>
            {skills.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="setup-selected-skills">
                {skills.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                    {s}
                    <button onClick={() => toggleSkill(s)} className="text-amber-600 hover:text-amber-800"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
              <Input placeholder="Search skills…" value={skillSearch} onChange={(e) => setSkillSearch(e.target.value)} className="pl-10" data-testid="setup-skills-search" />
            </div>
            <div className="max-h-80 overflow-y-auto space-y-3 rounded-lg border border-neutral-100 p-3">
              {filteredCatalog.map((g) => (
                <div key={g.group}>
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">{g.group}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {g.items.map((s) => {
                      const sel = skills.includes(s);
                      return (
                        <button
                          key={s}
                          onClick={() => toggleSkill(s)}
                          className={`rounded-full border-2 px-2 py-0.5 text-xs transition ${sel ? "border-amber-700 bg-amber-700 text-white" : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300"}`}
                          data-testid={`skill-${s.replace(/[^A-Za-z0-9]/g, "-")}`}
                        >
                          {sel && <Check className="mr-1 inline h-3 w-3" />}{s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={skip}>Skip</Button>
                <Button onClick={() => next({ skills })} className="bg-amber-700 hover:bg-amber-800" disabled={busy || skills.length < 3} data-testid="setup-next-3">
                  Next <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4 — Finishing touches */}
      {step === 4 && (
        <Card data-testid="setup-step-4">
          <CardContent className="space-y-4 p-6">
            <h2 className="text-xl font-bold">Finishing touches</h2>
            <p className="text-sm text-neutral-500">Optional — you can always update these later.</p>
            <div className="space-y-1.5">
              <Label>LinkedIn / portfolio URL</Label>
              <Input value={profile.linkedin_url || ""} onChange={(e) => set({ linkedin_url: e.target.value })} placeholder="https://…" data-testid="setup-linkedin" />
            </div>
            <div className="space-y-1.5">
              <Label>Preferred task types</Label>
              <div className="flex flex-wrap gap-1.5">
                {TASK_TYPES.map((t) => {
                  const sel = (profile.preferred_task_types || []).includes(t.code);
                  return (
                    <button
                      key={t.code}
                      onClick={() => {
                        const cur = profile.preferred_task_types || [];
                        set({ preferred_task_types: sel ? cur.filter((c) => c !== t.code) : [...cur, t.code] });
                      }}
                      className={`rounded-lg border-2 px-3 py-1 text-xs font-medium ${sel ? "border-amber-700 bg-amber-50 text-amber-900" : "border-neutral-200 bg-white text-neutral-700"}`}
                      data-testid={`pref-task-${t.code}`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="rounded-lg border border-neutral-200 p-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!profile.english_only_ok}
                  onChange={(e) => set({ english_only_ok: e.target.checked })}
                  data-testid="setup-english-only"
                />
                Willing to accept English-only tasks
              </label>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(3)}><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
              <Button onClick={finish} className="bg-amber-700 hover:bg-amber-800" disabled={busy} data-testid="setup-complete-btn">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Complete profile
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
